import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Title, Filler);

ChartJS.defaults.font.family = '"DM Sans", system-ui, sans-serif';
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = "#5A6A94";

/** Varsayilan seri paleti — dataset kendi rengini VERMEDIYSE sirayla atanir:
 * "Bugun" mavi, "Dun" gri, sonra yesil/turuncu. */
const VARSAYILAN_PALET = ["#1A56DB", "#9CA3AF", "#16A34A", "#F59E0B"];

export type ChartValueKind = "money" | "int" | "float" | "percent";

function kisaSayi(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toLocaleString("tr-TR", { maximumFractionDigits: 1 }) + " M";
  if (abs >= 10_000) return (v / 1_000).toLocaleString("tr-TR", { maximumFractionDigits: 0 }) + " B";
  if (abs >= 1_000) return (v / 1_000).toLocaleString("tr-TR", { maximumFractionDigits: 1 }) + " B";
  return v.toLocaleString("tr-TR", { maximumFractionDigits: 1 });
}

/** Eksen etiketi: kisa (₺12 B); tooltip: tam (₺12.345). */
export function fmtChartValue(v: number, kind: ChartValueKind, full = false): string {
  if (kind === "money") return full ? "₺" + v.toLocaleString("tr-TR", { maximumFractionDigits: 0 }) : "₺" + kisaSayi(v);
  if (kind === "percent") return v.toLocaleString("tr-TR", { maximumFractionDigits: 1 }) + "%";
  if (kind === "float") return v.toLocaleString("tr-TR", { maximumFractionDigits: 1 });
  return full ? v.toLocaleString("tr-TR") : kisaSayi(v);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- dataset alanlari bar/line arasinda tip olarak ayrisir; sarmalayici bilerek gevsek tipli.
function paletUygula(data: ChartData<"line"> | ChartData<"bar">, type: "line" | "bar"): any {
  const datasets = (data.datasets ?? []) as unknown as Array<Record<string, unknown>>;
  return {
    ...data,
    datasets: datasets.map((ds, i) => {
      const renk = VARSAYILAN_PALET[i % VARSAYILAN_PALET.length];
      const ortak = {
        ...ds,
        backgroundColor: ds.backgroundColor ?? renk,
        borderColor: ds.borderColor ?? renk,
      };
      if (type === "line") {
        return {
          tension: 0.32,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: i === 0 ? 2.5 : 2,
          spanGaps: true,
          ...ortak,
        };
      }
      return { borderRadius: 3, maxBarThickness: 28, borderSkipped: false, ...ortak };
    }),
  };
}

/** `valueKind` yetmediginde (sure, gun...) eksen + tooltip bicimini caller verir. */
export interface ChartValueFormat {
  tick: (v: number) => string;
  tooltip: (v: number) => string;
}

interface ChartCardProps {
  title?: string;
  note?: string;
  type?: "line" | "bar";
  data: ChartData<"line"> | ChartData<"bar">;
  /** Seyrek etiket: yalniz her N etiketten birini goster (kalabalik x eksenini onler). */
  labelEvery?: number;
  height?: number;
  /** y ekseni / tooltip bicimi. */
  valueKind?: ChartValueKind;
  /** `valueKind` yerine ozel bicim (ör. gun/saat). */
  format?: ChartValueFormat;
  /** Tooltip basligi — varsayilan x etiketi. */
  tooltipTitle?: (index: number) => string;
  /** Tooltip govdesine eklenen ek satirlar (ör. "63 sipariş"). */
  tooltipLines?: (index: number) => string[];
  /** Bir noktaya/sutuna tiklaninca (index = x etiketi sirasi); imlec el olur. */
  onPointClick?: (index: number) => void;
  /** Kart cercevesi olmadan yalniz canvas (bir kartin icine gomulurken). */
  bare?: boolean;
  /** Bar grafikte serileri ust uste yigar. */
  stacked?: boolean;
}

interface ChartHitElement {
  index: number;
}

interface ChartNativeEvent {
  native?: { target?: { style?: { cursor?: string } } | null } | null;
}

/**
 * react-chartjs-2 sarmalayicisi — tum grafiklerin ortak gorunumu (grid rengi,
 * tipografi, para/yuzde bicimi, legend). jsdom'da canvas olmadigindan
 * TESTLERDE render EDILMEZ; secenekler cizim turune gore generic ayristigi
 * icin bilerek gevsek (`any`) tutulur.
 */
export function ChartCard({
  title,
  note,
  type = "line",
  data,
  labelEvery = 1,
  height = 220,
  valueKind = "int",
  format,
  tooltipTitle,
  tooltipLines,
  onPointClick,
  bare = false,
  stacked = false,
}: ChartCardProps) {
  const cokSeri = (data.datasets?.length ?? 0) > 1;
  const tickFmt = (v: number) => (format ? format.tick(v) : fmtChartValue(v, valueKind));
  const tooltipFmt = (v: number) => (format ? format.tooltip(v) : fmtChartValue(v, valueKind, true));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Chart.js secenekleri cizim turune gore generic ayrisir.
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    onClick: onPointClick
      ? (_evt: unknown, elements: ChartHitElement[]) => {
          if (elements.length > 0) onPointClick(elements[0].index);
        }
      : undefined,
    onHover: onPointClick
      ? (evt: ChartNativeEvent, elements: ChartHitElement[]) => {
          const style = evt.native?.target?.style;
          if (style) style.cursor = elements.length > 0 ? "pointer" : "default";
        }
      : undefined,
    plugins: {
      legend: {
        display: cokSeri,
        position: "top",
        align: "end",
        labels: { boxWidth: 12, boxHeight: 12, usePointStyle: type === "line", pointStyle: "line", padding: 14 },
      },
      title: { display: false },
      tooltip: {
        backgroundColor: "#0F2044",
        titleFont: { weight: "bold" },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          title: tooltipTitle ? (items: { dataIndex: number }[]) => (items.length > 0 ? tooltipTitle(items[0].dataIndex) : "") : undefined,
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) =>
            `${ctx.dataset.label ? ctx.dataset.label + ": " : ""}${ctx.parsed.y == null ? "—" : tooltipFmt(ctx.parsed.y)}`,
          afterBody: tooltipLines ? (items: { dataIndex: number }[]) => (items.length > 0 ? tooltipLines(items[0].dataIndex) : []) : undefined,
        },
      },
    },
    scales: {
      x: {
        stacked,
        grid: { display: false },
        border: { color: "#E0E7FF" },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          callback: (_value: unknown, index: number) =>
            labelEvery <= 1 || index % labelEvery === 0 ? String(data.labels?.[index] ?? "") : "",
        },
      },
      y: {
        stacked,
        beginAtZero: true,
        grid: { color: "#E0E7FF" },
        border: { display: false, dash: [3, 3] },
        ticks: { maxTicksLimit: 6, callback: (v: unknown) => (typeof v === "number" ? tickFmt(v) : String(v)) },
      },
    },
  };

  const paletliVeri = paletUygula(data, type);
  const canvas = (
    <div style={{ height }} className="relative w-full min-w-0 overflow-hidden">
      {type === "bar" ? <Bar data={paletliVeri} options={options as ChartOptions<"bar">} /> : <Line data={paletliVeri} options={options as ChartOptions<"line">} />}
    </div>
  );

  if (bare) return canvas;

  return (
    <div className="card">
      {(title || note) && (
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          {title && <h3 className="font-display text-[15px] font-bold text-ink">{title}</h3>}
          {note && <span className="text-xs text-muted">{note}</span>}
        </div>
      )}
      {canvas}
    </div>
  );
}
